import { useEffect, useState } from 'react'
import DocLayout from '../../components/docs/DocLayout'
import CopyButton from '../../components/docs/CopyButton'
import { tokens } from '@/content/system'
import { densityStyles, usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

const titleCase = (value: string) => value[0].toUpperCase() + value.slice(1)

/** Widths where layouts change, smallest first. */
const breakpoints = [
  { name: 'Phone', from: '0', query: '', changes: 'One column. Menus fold into the header, and the docs menu sits at the top of the page.' },
  {
    name: 'Tablet',
    from: '48rem (768px)',
    query: '(min-width: 48rem)',
    changes: 'Side menus appear beside the content, and paired sections and grids go two across.',
  },
  {
    name: 'Desktop',
    from: '64rem (1024px)',
    query: '(min-width: 64rem)',
    changes: 'Wide layouts: three-across grids, sticky in-page navigation and the full header links.',
  },
]

const evenGrid = `/* As many columns as fit, but never more than three, so six items
   sit three and three, or two by two by two on a narrow column. */
.my-grid {
  --gap: var(--mds-space-4);
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, max(12rem, (100% - 2 * var(--gap)) / 3)), 1fr)
  );
  gap: var(--gap);
}`

/** The breakpoint the visitor's window is at now. */
function useCurrentBreakpoint() {
  const read = () => {
    try {
      return [...breakpoints].reverse().find((point) => !point.query || window.matchMedia(point.query).matches)?.name ?? 'Phone'
    } catch {
      return 'Phone'
    }
  }
  const [current, setCurrent] = useState(read)
  useEffect(() => {
    const update = () => setCurrent(read())
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  return current
}

export default function LayoutPage() {
  const { prefs, set } = usePrefs()
  const space = tokens.filter((token) => token.group === 'spacing')
  const layout = tokens.filter((token) => token.group === 'layout')
  const current = useCurrentBreakpoint()

  return (
    <DocLayout
      section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Layout"
      intro={
        <p>
          A four-point spacing scale, two densities, one centered column with a fluid gutter, two breakpoints and
          plain grids that reflow to a single column at 320px. Every page in MDS follows these rules, and so can any
          site built with it.
        </p>
      }
      wide
    >
      <section className="doc-section" aria-labelledby="space-scale">
        <h2 id="space-scale"><Cased>Spacing scale</Cased></h2>
        <p>
          Steps are multiples of 4px. Small steps relate things inside a component; large steps separate sections.
          Component padding multiplies by the density token.
        </p>
        <ol className="fnd-space">
          {space.map((token) => (
            <li key={token.css} className="fnd-space__row">
              <code>{token.css}</code>
              <span className="fnd-space__value">{token.value}</span>
              <span className="fnd-space__bar" style={{ width: `var(${token.css})` }} aria-hidden="true" />
              <span className="mds-muted">{token.role}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="doc-section" aria-labelledby="space-density">
        <h2 id="space-density"><Cased>Density</Cased></h2>
        <p>
          Comfortable suits public services and first-time visitors. Compact suits staff tools and data-dense screens.
          Density scales component padding and section spacing; type size and touch targets stay the same.
        </p>
        <div className="mds-segmented" role="group" aria-label="Density">
          {densityStyles.map((value) => (
            <button
              key={value}
              type="button"
              className="mds-segmented__option"
              aria-pressed={prefs.density === value}
              onClick={() => set('density', value)}
            >
              {titleCase(value)}
            </button>
          ))}
        </div>
        <div className="doc-specimen fnd-density">
          <div className="mds-card mds-card--raised">
            <span className="mds-tag">Benefits</span>
            <p className="mds-card__title">Check your application status</p>
            <label className="mds-field">
              <span className="mds-label">Confirmation number</span>
              <input className="mds-input" placeholder="Example: A1B2C3" />
            </label>
            <div className="mds-cluster">
              <span className="mds-button mds-button--primary">Check status</span>
              <span className="mds-button mds-button--ghost">Get help</span>
            </div>
          </div>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="layout-columns">
        <h2 id="layout-columns"><Cased>Columns and gutters</Cased></h2>
        <p>
          Content sits in one centered column, <code>.mds-container</code>, with a side gutter that grows with the
          window. Reading text takes a narrower measure inside it, so lines stay comfortable to read.
        </p>
        <div className="doc-table-wrap" tabIndex={0}>
          <table className="doc-table">
            <thead>
              <tr>
                <th scope="col">Token</th>
                <th scope="col">Value</th>
                <th scope="col">Use</th>
              </tr>
            </thead>
            <tbody>
              {layout.map((token) => (
                <tr key={token.css}>
                  <th scope="row">
                    <code>{token.css}</code>
                  </th>
                  <td>
                    <code>{token.value}</code>
                  </td>
                  <td className="mds-muted">{token.role}</td>
                </tr>
              ))}
              <tr>
                <th scope="row">
                  <code>--mds-gutter</code>
                </th>
                <td>
                  <code>clamp(1rem, 0.4rem + 3vw, 3.5rem)</code>
                </td>
                <td className="mds-muted">Side gutter, from 16px on a phone to 56px on a desktop.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="layout-breakpoints">
        <h2 id="layout-breakpoints"><Cased>Breakpoints</Cased></h2>
        <p>
          Two widths change the layout. Styles start from the phone and add to it as the window widens, in rem so
          they follow the visitor's text size. Your window is at <strong>{current}</strong> now.
        </p>
        <div className="doc-table-wrap" tabIndex={0}>
          <table className="doc-table">
            <thead>
              <tr>
                <th scope="col">Size</th>
                <th scope="col">From</th>
                <th scope="col">What changes</th>
              </tr>
            </thead>
            <tbody>
              {breakpoints.map((point) => (
                <tr key={point.name} aria-current={point.name === current ? 'true' : undefined}>
                  <th scope="row">{point.name}</th>
                  <td>
                    <code>{point.from}</code>
                  </td>
                  <td className="mds-muted">{point.changes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="layout-grids">
        <h2 id="layout-grids"><Cased>Even grids</Cased></h2>
        <p>
          Grids fit as many columns as the space allows, with a cap chosen from the item count, so rows stay even: six
          items sit three and three, four sit two by two. A lone item on the last row reads as a mistake.
        </p>
        <div className="fnd-layout-demo" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
        <pre className="mds-code" tabIndex={0} aria-label="Even grid CSS">
          <code>{evenGrid}</code>
        </pre>
        <CopyButton text={evenGrid} label="Copy grid CSS" />
      </section>

      <section className="doc-section" aria-labelledby="layout-reflow">
        <h2 id="layout-reflow"><Cased>Reflow</Cased></h2>
        <p>
          Every page works at 320px wide, the width of a phone or of a desktop window zoomed to 400%, with no sideways
          scrolling (WCAG 1.4.10). Wide tables and code scroll inside their own focusable region instead, so the page
          around them still fits.
        </p>
      </section>

      <section className="doc-section" aria-labelledby="layout-sticky">
        <h2 id="layout-sticky"><Cased>Sticky header</Cased></h2>
        <p>
          The header stays at the top as the page scrolls. Its height is <code>--mds-header-height</code>, and the page
          keeps a matching scroll padding, so a link to a section lands with its heading below the header rather than
          under it.
        </p>
      </section>
    </DocLayout>
  )
}
